import { useEffect, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../Redux/redux-store";
import {
  DrivePanelState,
  GetCurrentObj,
  GetFilterText,
  GetLayout,
  GetLoadingStates,
  GetPagination,
  GetPanelState,
  GetScale,
  GetScales,
  GetSearchResults,
  ResetPanelObject,
  SetActiveScale,
  SetFilterText,
  SetIsSearch,
  SetLayout,
  SetPanelState,
  SetScales,
  SetSearchResults,
} from "../../../Redux/Reducers/redux-slice-drive";
import { Box, Chip, IconButton, InputAdornment, OutlinedInput, Stack, Typography } from "@mui/material";
import ScaleSlider from "../../Misc/ScaleSlider";
import ToggleLayout from "../../Misc/ToggleLayout";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../Misc/ListPageHeader";
import { CloseOutlined, FilterAltOutlined } from "@mui/icons-material";
import { GetPattern, SetSearchPattern } from "../../../Redux/Reducers/redux-slice-filters";
import { useTranslation } from "react-i18next";
import FiltersList from "../../Filters/FiltersList/filters-list";
import { ObjectLayer } from "../../../Core/Api/Types/object-layer";
import { MetadataFieldValueObjectTypes } from "../../../Core/Api/Types/metadata-field-value-object-types";

export const DriveSearchHeader: React.FC = () => {
  const dispatch = useAppDispatch();
  const pattern = useAppSelector(GetPattern);
  const searchHeaderRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const scale = useAppSelector(GetScale);
  const scales = useAppSelector(GetScales);
  const layout = useAppSelector(GetLayout);
  const enlargedPanel = useAppSelector(GetPanelState) === DrivePanelState.Enlarged;
  const { folderLoading } = useAppSelector(GetLoadingStates);
  const previewPanelOpen = useAppSelector(GetPanelState) === DrivePanelState.Preview;
  const filterText = useAppSelector(GetFilterText);
  const searchResults = useAppSelector(GetSearchResults);
  const currentObject = useAppSelector(GetCurrentObj);

  const { totalChildrenCount } = useAppSelector(GetPagination);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((event) => {
      // recalculate scales to avoid very small asset cards
      // 6 colonnes = > 1000
      // 4 colonnes = >650
      // 3 colonnes = >500
      // 2 colonnes = >340
      if (event[0].contentBoxSize[0].inlineSize < 340) {
        if (scale === 2 || scale === 3 || scale === 4 || scale === 6) dispatch(SetActiveScale(12));
        dispatch(SetScales([12]));
      } else if (event[0].contentBoxSize[0].inlineSize < 500) {
        if (scale === 2 || scale === 3 || scale === 4) dispatch(SetActiveScale(6));
        dispatch(SetScales([6, 12]));
      } else if (event[0].contentBoxSize[0].inlineSize < 650) {
        if (scale === 2 || scale === 3) dispatch(SetActiveScale(4));
        dispatch(SetScales([4, 6, 12]));
      } else if (event[0].contentBoxSize[0].inlineSize < 1000) {
        if (scale === 2) dispatch(SetActiveScale(3));
        dispatch(SetScales([3, 4, 6, 12]));
      } else {
        dispatch(SetScales([2, 3, 4, 6, 12]));
      }
    });

    if (searchHeaderRef.current) resizeObserver.observe(searchHeaderRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [folderLoading, layout, enlargedPanel]);

  const handleQuitSearch = () => {
    dispatch(SetSearchPattern(""));
    dispatch(SetSearchResults([]));
    dispatch(SetIsSearch(false));
    dispatch(ResetPanelObject());
    dispatch(SetPanelState(DrivePanelState.Closed));
  };

  return (
    <Box
      ref={searchHeaderRef}
      sx={{
        backgroundColor: "background.default",
        display: "flex",
        flexDirection: "column",
        py: 1,
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={2}>
        <Stack direction="row" gap={0.5} alignItems={"center"}>
          <IconButton color="ghost" size="small" onClick={handleQuitSearch}>
            <CloseOutlined />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} color="text.primary">
            {t("component:searchResults", { pattern: pattern })}
          </Typography>
          {totalChildrenCount !== 0 && <Chip sx={{ ml: 1 }} label={totalChildrenCount} />}
        </Stack>
        <Stack direction="row" gap={1}>
          {layout !== "list" && (
            <ScaleSlider scales={scales} scale={scale} setScale={(scale) => dispatch(SetActiveScale(scale))} />
          )}
          <ToggleLayout
            value={layout}
            onChange={(e: React.MouseEvent<HTMLElement>, value: "grid" | "list") => dispatch(SetLayout(value))}
          />
        </Stack>
      </Stack>

      <ListPageHeader sx={{ px: 2, overflowY: "visible", display: enlargedPanel ? "none" : "flex" }}>
        <ListPageHeaderLeft sx={{}}>
          <FiltersList layer={ObjectLayer.Asset} type={MetadataFieldValueObjectTypes.DbAssetObjectInfos} />
        </ListPageHeaderLeft>

        <ListPageHeaderRight>
          <OutlinedInput
            label={t("field:label.filterEllipsis")}
            placeholder={t("field:label.filterEllipsis")}
            value={filterText}
            sx={{ width: previewPanelOpen ? "112px" : "219px" }}
            size="small"
            onChange={(value) => dispatch(SetFilterText(value.target.value))}
            endAdornment={
              <InputAdornment position="end">
                <FilterAltOutlined />
              </InputAdornment>
            }
          />
        </ListPageHeaderRight>
      </ListPageHeader>
    </Box>
  );
};
